import React, { useEffect, useRef } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { WaveDataPoint } from '../../types'

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

interface WaveChartProps {
  data: WaveDataPoint[]
  height?: number
  animated?: boolean
  showTooltips?: boolean
}

const WaveChart: React.FC<WaveChartProps> = ({ 
  data, 
  height = 400, 
  animated = true, 
  showTooltips = true 
}) => {
  const chartRef = useRef<ChartJS<'line', number[], string>>(null)

  useEffect(() => {
    if (animated && chartRef.current) {
      chartRef.current.update('active')
    }
  }, [data, animated])

  const chartData = {
    labels: data.map(point => point.date || point.week),
    datasets: [
      {
        label: 'Market Sentiment Wave',
        data: data.map(point => point.sentiment),
        borderColor: (context: { chart: { ctx: CanvasRenderingContext2D, chartArea: { bottom: number, top: number } | null } }) => {
          const chart = context.chart
          const { ctx, chartArea } = chart
          
          if (!chartArea) {
            return '#3b82f6'
          }
          
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top)
          gradient.addColorStop(0, '#ef4444') // Bearish (red)
          gradient.addColorStop(0.5, '#6b7280') // Neutral (gray)
          gradient.addColorStop(1, '#22c55e') // Bullish (green)
          
          return gradient
        },
        backgroundColor: (context: { chart: { ctx: CanvasRenderingContext2D, chartArea: { bottom: number, top: number } | null } }) => {
          const chart = context.chart
          const { ctx, chartArea } = chart
          
          if (!chartArea) {
            return 'rgba(59, 130, 246, 0.1)'
          }
          
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top)
          gradient.addColorStop(0, 'rgba(239, 68, 68, 0.1)') // Bearish (red)
          gradient.addColorStop(0.5, 'rgba(107, 114, 128, 0.05)') // Neutral (gray)
          gradient.addColorStop(1, 'rgba(34, 197, 94, 0.1)') // Bullish (green)
          
          return gradient
        },
        borderWidth: 3,
        pointBackgroundColor: data.map(point => 
          point.isBullish ? '#22c55e' : '#ef4444'
        ),
        pointBorderColor: data.map(point => 
          point.isBullish ? '#16a34a' : '#dc2626'
        ),
        pointBorderWidth: 2,
        pointRadius: data.map(point => Math.min(3 + point.strength * 2, 6)), // Smaller points
        pointHoverRadius: data.map(point => Math.min(4 + point.strength * 2, 8)),
        fill: true,
        tension: 0.4, // Smooth curve
      }
    ]
  }

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10
      }
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Time Period',
          color: '#6b7280',
          font: {
            size: 12,
            weight: 500,
          },
        },
        grid: {
          color: 'rgba(107, 114, 128, 0.1)',
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 11,
          },
        },
      },
      y: {
        display: true,
        min: -1.05,
        max: 1.05,
        title: {
          display: true,
          text: 'Market Sentiment',
          color: '#6b7280',
          font: {
            size: 12,
            weight: 500,
          },
        },
        grid: {
          color: (context: { tick: { value: number } }) => {
            if (context.tick.value === 0) {
              return 'rgba(107, 114, 128, 0.3)' // Stronger line at zero
            }
            return 'rgba(107, 114, 128, 0.1)'
          },
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 11,
          },
          callback: function(value: string | number) {
            if (value === 1) return 'Very Bullish'
            if (value === 0.5) return 'Bullish'
            if (value === 0) return 'Neutral'
            if (value === -0.5) return 'Bearish'
            if (value === -1) return 'Very Bearish'
            return ''
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: showTooltips,
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#f9fafb',
        bodyColor: '#f9fafb',
        borderColor: 'rgba(107, 114, 128, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: function(context: { dataIndex: number }[]) {
            const point = data[context[0].dataIndex]
            return `${point.week} (${point.date})`
          },
          label: function(context: { dataIndex: number }) {
            const point = data[context.dataIndex]
            const sentiment = point.sentiment > 0 ? 'Bullish' : point.sentiment < 0 ? 'Bearish' : 'Neutral'
            const strength = Math.round(point.strength * 100)
            return [
              `Sentiment: ${sentiment}`,
              `Score: ${point.sentiment.toFixed(2)}`,
              `Strength: ${strength}%`
            ]
          },
        },
      },
    },
    animation: animated ? {
      duration: 2000,
      easing: 'easeInOutQuart' as const,
      delay: (context: { dataIndex: number }) => context.dataIndex * 100, // Stagger animation
    } : false,
    elements: {
      line: {
        borderCapStyle: 'round' as const,
      },
      point: {
        hoverBorderWidth: 3,
      },
    },
  }

  return (
    <div className="relative">
      <div style={{ height: `${height}px`, overflow: 'hidden', position: 'relative' }}>
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
      
      {/* Legend */}
      <div className="flex justify-center mt-4 space-x-8">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-bullish-500"></div>
          <span className="text-sm text-gray-600">Bullish Weeks</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-bearish-500"></div>
          <span className="text-sm text-gray-600">Bearish Weeks</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-gray-400"></div>
          <span className="text-sm text-gray-600">Neutral</span>
        </div>
      </div>
    </div>
  )
}

export default WaveChart